import _ from 'underscore';
import { useState, useRef } from 'react';
import styled from 'styled-components';
import { WrTooltip } from '../../../styled-components/common/wr-tooltip';
import AssetPickerGuideTask from './AssetPickerGuideTask';
import AssetPickerTypes from './asset-picker-types';
import LearningObjectIcon from './LearningObjectIcon';

interface Section {
  id: string;
  title: string;
  courseSectionTasks?: Array<Task>;
}

interface Task {
  id: string;
}

interface Props {
  section: Section;
  currentAsset: Task | Task[];
  isActive: boolean;
  isActiveById: { [key: string]: boolean };
  viewableAssets: string[];
  selectableAssets: { [key: string]: (section: Section) => void };
}

const AssetPickerGuideSection = ({
  section,
  currentAsset,
  isActive,
  isActiveById,
  viewableAssets,
  selectableAssets,
}: Props) => {
  const sectionNameRef = useRef<HTMLDivElement>(null);

  // Calculate initial expanded state
  const getInitialExpandedState = () => {
    if (!currentAsset) return false;

    const isCurrentAssetInTask = (asset: Task, task: Task) => task.id === asset.id;
    const assets = Array.isArray(currentAsset) ? currentAsset : [currentAsset];

    return assets.some((asset) => section.courseSectionTasks?.some((task) => isCurrentAssetInTask(asset, task)));
  };

  const [isExpanded, setIsExpanded] = useState(getInitialExpandedState());

  const showTasks = () => viewableAssets.includes(AssetPickerTypes.GUIDE_TASK);

  const getTasks = () => {
    const tasks = section.courseSectionTasks;
    if (!tasks?.length) return null;

    return (
      <ul className="asset-picker-asset--children asset-picker-guide--tasks">
        {_.map(tasks, (task) => (
          <AssetPickerGuideTask
            selectableAssets={selectableAssets}
            key={task.id}
            task={task}
            isActive={isActiveById[task.id]}
          />
        ))}
      </ul>
    );
  };

  const handleSelectSectionClick = () => {
    const callback = selectableAssets[AssetPickerTypes.GUIDE_SECTION];
    if (callback) callback(section);
    setIsExpanded(!isExpanded);
  };

  const activeClass = isActive ? 'asset-picker-asset--active' : '';
  const tasks = showTasks() && isExpanded ? getTasks() : '';
  const name = section.title || 'Untitled Section';

  // TODO: Using LearningObjectIcon because ContentIcon is missing GUIDE_SECTION icon
  return (
    <div className={`asset-picker-asset asset-picker-guide-section ${activeClass}`}>
      <div className="asset-picker-asset--header" ref={sectionNameRef}>
        <StyledButton
          type="button"
          style={{ textAlign: 'left', border: 'none', padding: 0, background: 'none' }}
          className="asset-picker-asset--label"
          onClick={handleSelectSectionClick}
        >
          <LearningObjectIcon
            type={AssetPickerTypes.GUIDE_SECTION}
            iconClassName="asset-picker-asset--icon"
            height="16px"
            width="16px"
            style={{}}
          />
          <div className="asset-picker-asset--name">
            <WrTooltip arrow={false} content={name} direction="down-start" tagName="span" zIndex={2000} inline>
              {name}
            </WrTooltip>
          </div>
        </StyledButton>
      </div>
      {tasks}
    </div>
  );
};

const StyledButton = styled.button`
  text-align: left;
  border: none;
  background: none;
`;

export default AssetPickerGuideSection;
